import React, { useMemo, useRef, useState } from "react";
import { CornerFrame, GoldDivider } from "../../components/BrandDecor/BrandDecor";
import { SELF_CHECK_RESULT_THRESHOLD, SELF_CHECK_SECTIONS } from "../../data/selfCheck";
import { submitSelfCheckLead } from "../../api/selfCheck";
import { nvfoWechatQR, nvfoWhatsappQR } from "../../assets/imgExport";
import { BROCHURE_PDF_URL } from "../../config/brochure";

const TRIGGER_SCENARIOS = [
  "孩子要出国读书，要不要提前准备身份？",
  "准备在海外置业，用个人名义还是公司架构？",
  "家庭资产越来越多，未来税务怎么安排？",
  "企业准备国际化，要不要提前布局海外身份？",
  "子女未来家庭资产怎么传承？",
];

const RESULT_BENEFITS = [
  "价值300镑的咨询服务（免费）",
  "1000镑的后续服务优惠抵扣金",
  "全球投资移民项目手册",
];

export default function SelfCheckPage() {
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const resultRef = useRef<HTMLDivElement>(null);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const checkedCount = useMemo(
    () => Object.values(checked).filter(Boolean).length,
    [checked],
  );

  const qualifies = checkedCount >= SELF_CHECK_RESULT_THRESHOLD;

  function toggleOption(sectionId: string, optionKey: string) {
    const compositeKey = `${sectionId}::${optionKey}`;
    setChecked((prev) => ({ ...prev, [compositeKey]: !prev[compositeKey] }));
  }

  function scrollToResult() {
    resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setError("请填写您的姓名");
      return;
    }
    if (email.trim() && !/^\S+@\S+\.\S+$/.test(email)) {
      setError("请输入有效的邮箱地址");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const selections: Record<string, string[]> = {};
      SELF_CHECK_SECTIONS.forEach((section) => {
        const labels = section.options
          .filter((option) => checked[`${section.id}::${option.key}`])
          .map((option) => option.label);
        if (labels.length) selections[section.title] = labels;
      });

      await submitSelfCheckLead({
        name,
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        checkedCount,
        selections,
      });
      setSubmitted(true);
    } catch (err) {
      setError("提交失败，请稍后重试。");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-c-gray min-h-screen pb-24">
      <div className="relative bg-secondary-dark text-white px-6 sm:px-20 py-20 sm:py-28 overflow-hidden">
        <CornerFrame />
        <div className="relative z-10 max-width-centered">
          <p className="inline-flex items-center gap-2 text-xs sm:text-sm tracking-[0.2em] uppercase text-gold-light border border-gold/40 rounded-full px-4 py-2 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-gold" />
            家庭自查表
          </p>
          <h1 className="text-2xl sm:text-3xl font-main-sans mb-2 tracking-wide">
            海外身份与全球资产规划自查表
          </h1>
          <GoldDivider toneClassName="bg-gold/70" />
          <h2 className="text-lg sm:text-xl text-white/90 mb-4">
            您和您的家庭，需要的可能不只是一个海外身份
          </h2>
          <p className="text-white/70 leading-relaxed max-w-2xl mb-6">
            很多家庭最初考虑海外规划，往往从一个具体问题开始：
          </p>
          <ul className="flex flex-col gap-2 max-w-2xl mb-6">
            {TRIGGER_SCENARIOS.map((scenario) => (
              <li key={scenario} className="flex items-start gap-3 text-white/80">
                <span className="w-1.5 h-1.5 rotate-45 bg-gold shrink-0 mt-2" />
                {scenario}
              </li>
            ))}
          </ul>
          <p className="text-white/70 leading-relaxed max-w-2xl">
            但真正进入规划之后会发现：身份从来不是一个独立的问题。它往往与家庭的教育、资产配置、房产、企业股权、税务居民身份以及财富传承紧密相关。所以在决定"去哪个国家、投多少钱"之前，不妨先用这张表，对自己和家庭做一次系统自查。
          </p>
        </div>
      </div>

      <div className="max-width-centered px-6 sm:px-20 py-14 sm:py-20 flex flex-col gap-10">
        {SELF_CHECK_SECTIONS.map((section, index) => (
          <div key={section.id} className="bg-white rounded-2xl p-7 sm:p-10">
            <div className="flex items-start gap-5 mb-6">
              <p className="font-main-serif text-3xl sm:text-4xl text-gold/50 shrink-0">
                {String(index + 1).padStart(2, "0")}
              </p>
              <div>
                <h3 className="text-lg sm:text-xl text-secondary-dark">{section.title}</h3>
                {section.subtitle && (
                  <p className="text-sm text-secondary-dark/60 mt-1.5">{section.subtitle}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              {section.options.map((option) => {
                const compositeKey = `${section.id}::${option.key}`;
                const isChecked = !!checked[compositeKey];
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => toggleOption(section.id, option.key)}
                    aria-pressed={isChecked}
                    className={`flex items-center gap-3 text-left rounded-xl border px-4 py-3.5 transition-colors ${
                      isChecked
                        ? "border-gold bg-gold/10"
                        : "border-fourth-light hover:border-gold/50"
                    }`}
                  >
                    <span
                      className={`w-4 h-4 rounded-sm border shrink-0 flex items-center justify-center text-[10px] ${
                        isChecked ? "bg-gold border-gold text-white" : "border-secondary-dark/30"
                      }`}
                    >
                      {isChecked && "✓"}
                    </span>
                    <span className="text-sm text-secondary-dark/85">{option.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="border-l-2 border-gold/60 bg-gold/5 rounded-r-lg px-4 py-3.5">
              <p className="text-xs tracking-[0.1em] uppercase text-gold mb-1.5">
                新愿景家族办公室提示
              </p>
              <p className="text-sm text-secondary-dark/75 leading-relaxed">{section.tip}</p>
            </div>
          </div>
        ))}

        <div ref={resultRef} className="bg-secondary-dark text-white rounded-2xl p-8 sm:p-12">
          <p className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-gold-light border border-gold/40 rounded-full px-4 py-1.5 mb-6">
            自查结果
          </p>
          <p className="text-white/70 mb-2">您已勾选</p>
          <p className="text-4xl font-main-serif text-gold mb-6">{checkedCount} 项</p>

          {qualifies ? (
            <p className="text-white/85 leading-relaxed mb-8">
              符合5个项目以上，说明您的家庭需要家族办公室服务一对一咨询并提供针对性综合服务。留下您的联系方式，我们会为您免费提供咨询包：
            </p>
          ) : (
            <p className="text-white/85 leading-relaxed mb-8">
              继续勾选以上符合您家庭情况的选项。如果符合5个项目以上，通常说明您的需求已经超出单一投资移民项目的范畴。留下联系方式，我们也可以为您提供一对一初步评估：
            </p>
          )}

          <ul className="flex flex-col gap-3 mb-10">
            {RESULT_BENEFITS.map((benefit, i) => (
              <li key={benefit} className="flex items-start gap-3 text-white/85">
                <span className="font-main-serif text-gold shrink-0">{i + 1}.</span>
                {benefit}
              </li>
            ))}
          </ul>

          {submitted ? (
            <div className="bg-white/10 border border-gold/40 rounded-xl p-6 sm:p-8">
              <p className="text-white mb-6">感谢您的留言，我们的顾问团队将尽快与您联系。以下是为您准备的内容：</p>
              <div className="flex flex-col sm:flex-row gap-8 items-start">
                <div className="flex gap-6 shrink-0">
                  <div className="flex flex-col items-center gap-2">
                    <img
                      src={nvfoWechatQR}
                      alt="微信咨询二维码"
                      className="h-24 w-auto max-w-none"
                      style={{ filter: "invert(1)", mixBlendMode: "screen" }}
                    />
                    <span className="text-xs text-white/60">微信咨询</span>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <img
                      src={nvfoWhatsappQR}
                      alt="WhatsApp咨询二维码"
                      className="h-24 w-auto max-w-none"
                      style={{ filter: "invert(1)", mixBlendMode: "screen" }}
                    />
                    <span className="text-xs text-white/60">WhatsApp咨询</span>
                  </div>
                </div>
                <ul className="flex flex-col gap-3 flex-1">
                  {RESULT_BENEFITS.map((benefit, i) => (
                    <li key={benefit} className="flex items-start justify-between gap-3 text-white/85 bg-white/5 rounded-lg px-4 py-3">
                      <span className="flex items-start gap-3">
                        <span className="font-main-serif text-gold shrink-0">{i + 1}.</span>
                        {benefit}
                      </span>
                      {i === RESULT_BENEFITS.length - 1 && (
                        <a
                          href={BROCHURE_PDF_URL}
                          target="_blank"
                          rel="noreferrer"
                          className="shrink-0 bg-gold hover:bg-gold-light text-secondary-dark text-xs font-medium px-3 py-1.5 rounded-full transition-colors whitespace-nowrap"
                        >
                          下载手册
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <form className="flex flex-col gap-4 max-w-md" onSubmit={handleSubmit}>
              <input
                className="bg-white/10 border border-white/25 rounded-lg px-4 py-3 text-white placeholder:text-white/40"
                placeholder="您的姓名"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <input
                className="bg-white/10 border border-white/25 rounded-lg px-4 py-3 text-white placeholder:text-white/40"
                placeholder="联系电话（选填）"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <input
                className="bg-white/10 border border-white/25 rounded-lg px-4 py-3 text-white placeholder:text-white/40"
                placeholder="邮箱地址（选填）"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="bg-white hover:bg-white/90 text-secondary-dark font-semibold py-3.5 rounded-lg transition-colors disabled:opacity-60"
              >
                {submitting ? "提交中…" : "领取免费咨询包"}
              </button>
            </form>
          )}
        </div>
      </div>

      <button
        onClick={scrollToResult}
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[999] flex items-center gap-3 bg-secondary-dark text-white rounded-full pl-5 pr-2 py-2 shadow-lg shadow-black/20"
      >
        <span className="text-sm text-white/80">已勾选 {checkedCount} 项</span>
        <span className="bg-gold text-secondary-dark text-sm font-medium rounded-full px-4 py-1.5">
          查看自查结果
        </span>
      </button>
    </div>
  );
}
